import type { ReactNode } from 'react';
import { assertNever } from '@signalvote/shared';
import { Confidence } from './voterEstimate';
import type { Estimate } from './voterEstimate';
import { useReducedMotion } from '@/hooks';
import { useSpringNumber } from './voterMotion';
import { formatDollars } from './voterPurpose';

const countText = (count: number) => Math.round(count).toLocaleString('en-US');

/** A voter count that springs to each new value from the one on screen; "–" before there is one. */
export function VoterCount({ value }: { value: number | undefined }) {
  if (value === undefined) return <>–</>;
  return <Springing value={value} format={countText} />;
}

/** How the builder sets an estimated number apart from an exact one. */
export function EstimateMark({ children }: { children: ReactNode }) {
  return (
    <span data-estimate className="italic">
      {children}
    </span>
  );
}

/** A number that springs to each new value from the one on screen, unless motion is reduced. */
function Springing({ value, format }: { value: number; format: (shown: number) => string }) {
  const shown = useSpringNumber(value, useReducedMotion());
  return <>{format(shown)}</>;
}

/**
 * A count or price as exact or estimated. An estimate reads "about" or as a range, in italics,
 * wherever it appears; an exact number has neither.
 */
export function VoterEstimate({ estimate, dollars = false }: { estimate: Estimate | undefined; dollars?: boolean }) {
  if (estimate === undefined) return <>–</>;
  return <SpringingEstimate estimate={estimate} dollars={dollars} />;
}

/**
 * The springs live here rather than in the markup, which differs with confidence, so an estimate
 * firming up glides to the exact number instead of jumping to it. The top of a range springs out
 * from the number before it.
 */
function SpringingEstimate({ estimate, dollars }: { estimate: Estimate; dollars: boolean }) {
  const reduced = useReducedMotion();
  const lead = useSpringNumber(estimate.confidence === Confidence.Range ? estimate.low : estimate.value, reduced);
  const high = useSpringNumber(estimate.confidence === Confidence.Range ? estimate.high : estimate.value, reduced);
  const wholeDollars = estimate.confidence !== Confidence.Exact;
  const format = (shown: number) => (dollars ? formatDollars(Math.round(shown), wholeDollars) : countText(shown));
  switch (estimate.confidence) {
    case Confidence.Exact:
      return <>{format(lead)}</>;
    case Confidence.About:
      return <EstimateMark>about {format(lead)}</EstimateMark>;
    case Confidence.Range:
      return (
        <EstimateMark>
          {format(lead)}–{format(high)}
        </EstimateMark>
      );
    default:
      return assertNever(estimate);
  }
}
